import React, { useState, useEffect } from 'react';
import {
  ArrowPathIcon,
  XMarkIcon,
  LinkIcon,
  UserIcon,
  PlusIcon,
  MinusIcon,
  CheckIcon,
  ArrowRightOnRectangleIcon,
} from '@heroicons/react/24/outline';
import { TanTable } from '../../components/TanTable';
import { ColumnDef } from '@tanstack/react-table';
import { Drawer } from '../../components/ui/Drawer';
import TypeAhead, { TypeAheadOption } from '../../components/TypeAhead';
import permissionsService, { AzureADUser, ForceLogoutStatus } from '../../services/permissions.service';
import impersonationService, { UserPermissionPreview } from '../../services/impersonation.service';
import yardService from '../../services/yard.service';
import { YardLocation } from '../../types/yard';
import { useMsal } from '@azure/msal-react';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { usePermissions } from '../../contexts/PermissionContext';

const Users: React.FC = () => {
  const [users, setUsers] = useState<AzureADUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AzureADUser | null>(null);
  const [userPreview, setUserPreview] = useState<UserPermissionPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // V1 user matching state
  const [v1Options, setV1Options] = useState<TypeAheadOption[]>([]);
  const [loadingV1Options, setLoadingV1Options] = useState(false);
  const [savingV1, setSavingV1] = useState(false);
  const [showV1Search, setShowV1Search] = useState(false);
  const [selectedV1Username, setSelectedV1Username] = useState('');

  // Home region state
  const [availableRegions, setAvailableRegions] = useState<string[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [savingHomeRegion, setSavingHomeRegion] = useState(false);
  const [savingHomeRegionDefault, setSavingHomeRegionDefault] = useState(false);

  // Default yard state
  const [availableYards, setAvailableYards] = useState<YardLocation[]>([]);
  const [loadingYards, setLoadingYards] = useState(false);
  const [savingDefaultYard, setSavingDefaultYard] = useState(false);
  const [savingIsLc, setSavingIsLc] = useState(false);

  // 360 Group management state
  const [showGroupPicker, setShowGroupPicker] = useState(false);
  const [availableGwi360Groups, setAvailableGwi360Groups] = useState<Array<{ id: string; displayName: string }>>([]);
  const [loadingGwi360Groups, setLoadingGwi360Groups] = useState(false);
  const [addingToGroup, setAddingToGroup] = useState(false);
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);

  // Force sign-out state
  const [forceLogoutStatus, setForceLogoutStatus] = useState<ForceLogoutStatus | null>(null);
  const [showForceLogoutConfirm, setShowForceLogoutConfirm] = useState(false);
  const [forceLogoutReason, setForceLogoutReason] = useState('');
  const [forcingLogout, setForcingLogout] = useState(false);
  const [forceLogoutResult, setForceLogoutResult] = useState<string | null>(null);

  // Impersonation
  const { startImpersonation, isLoading: isImpersonating } = useImpersonation();
  const { refreshPermissions } = usePermissions();
  const { accounts } = useMsal();
  const currentUserEmail = (accounts[0]?.username || '').toLowerCase();

  // Fetch users from the API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await permissionsService.getAllUsers(true); // Include hidden users
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Open user details drawer
  const handleViewDetails = async (user: AzureADUser) => {
    setSelectedUser(user);
    setDrawerOpen(true);
    setUserPreview(null);
    setShowV1Search(false);
    setV1Options([]);
    setSelectedV1Username('');
    setShowGroupPicker(false);
    setShowForceLogoutConfirm(false);
    setForceLogoutReason('');
    setForceLogoutResult(null);
    setForceLogoutStatus(null);

    // Fetch regions for home region dropdown
    fetchRegions();

    // Fetch yards for default yard dropdown
    fetchYards();

    // Last force sign-out, so an admin can see this user was already signed out
    // moments ago rather than firing again.
    permissionsService
      .getUserForceLogout(user.id)
      .then(setForceLogoutStatus)
      .catch(() => { /* non-critical: the button still works without history */ });

    try {
      setLoadingPreview(true);
      const preview = await impersonationService.previewUserPermissions(user.user_id);
      setUserPreview(preview);
    } catch (err: any) {
      // Silently fail - we'll show what we have
    } finally {
      setLoadingPreview(false);
    }
  };

  // Load all V1 users when opening the search (TypeAhead will filter locally)
  const openV1Search = async () => {
    setShowV1Search(true);
    setLoadingV1Options(true);

    try {
      // Fetch all active V1 users (empty query returns all)
      const results = await permissionsService.searchV1Users('');

      // Convert to TypeAhead options
      const options: TypeAheadOption[] = results.map(user => ({
        value: user.username,
        label: `${user.first || ''} ${user.last || ''}`.trim() || user.username,
        description: `${user.username}${user.email ? ` • ${user.email}` : ''}`,
      }));
      setV1Options(options);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load V1 users');
    } finally {
      setLoadingV1Options(false);
    }
  };

  // Handle V1 user selection from TypeAhead
  const handleV1UserSelect = async (username: string) => {
    if (!selectedUser || !username) return;

    setSelectedV1Username(username);

    try {
      setSavingV1(true);
      await permissionsService.updateUserV1Username(selectedUser.id, username);

      // Update local state
      const updatedUser = { ...selectedUser, v1_username: username };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));

      // Close the search
      setShowV1Search(false);
      setV1Options([]);
      setSelectedV1Username('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to link V1 user');
      setSelectedV1Username('');
    } finally {
      setSavingV1(false);
    }
  };

  // Unlink V1 user
  const handleUnlinkV1User = async () => {
    if (!selectedUser) return;

    try {
      setSavingV1(true);
      await permissionsService.updateUserV1Username(selectedUser.id, null);

      // Update local state
      const updatedUser = { ...selectedUser, v1_username: undefined };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unlink V1 user');
    } finally {
      setSavingV1(false);
    }
  };

  // Fetch available regions for home region dropdown
  const fetchRegions = async () => {
    if (availableRegions.length > 0) return; // Already loaded

    try {
      setLoadingRegions(true);
      const regions = await permissionsService.getAvailableRegions();
      setAvailableRegions(regions);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load regions');
    } finally {
      setLoadingRegions(false);
    }
  };

  // Handle home region change
  const handleHomeRegionChange = async (region: string) => {
    if (!selectedUser) return;

    const newRegion = region === '' ? null : region;

    try {
      setSavingHomeRegion(true);
      await permissionsService.updateUserHomeRegion(selectedUser.id, newRegion);

      // Update local state
      const updatedUser = { ...selectedUser, home_region: newRegion || undefined };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update home region');
    } finally {
      setSavingHomeRegion(false);
    }
  };

  // Handle 'Use as default' toggle
  const handleHomeRegionAsDefaultChange = async (value: boolean) => {
    if (!selectedUser) return;

    try {
      setSavingHomeRegionDefault(true);
      await permissionsService.updateUserHomeRegionAsDefault(selectedUser.id, value);

      const updatedUser = { ...selectedUser, home_region_as_default: value };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update home region default');
    } finally {
      setSavingHomeRegionDefault(false);
    }
  };

  // Toggle whether the user is an LC — i.e. appears in the record-level LC assignment
  // dropdowns (order / quote / Custom CSR Group / CMS Codes, via /cms-lookups/csrs).
  const handleIsLcChange = async (value: boolean) => {
    if (!selectedUser) return;

    try {
      setSavingIsLc(true);
      await permissionsService.updateUserIsLc(selectedUser.id, value);

      const updatedUser = { ...selectedUser, is_lc: value };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update LC flag');
    } finally {
      setSavingIsLc(false);
    }
  };

  // Fetch yards for default yard dropdown
  const fetchYards = async () => {
    if (availableYards.length > 0) return; // Already loaded

    try {
      setLoadingYards(true);
      const yards = await yardService.getYardLocations();
      setAvailableYards(yards);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load yards');
    } finally {
      setLoadingYards(false);
    }
  };

  // Handle default yard change
  const handleDefaultYardChange = async (yardValue: string) => {
    if (!selectedUser) return;

    const newYard = yardValue === '' ? null : Number(yardValue);

    try {
      setSavingDefaultYard(true);
      await permissionsService.updateUserDefaultYard(selectedUser.id, newYard);

      // Update local state
      const updatedUser = { ...selectedUser, default_yard: newYard ?? undefined };
      setSelectedUser(updatedUser);
      setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));

      // If the admin updated their own default_yard, refresh the cached
      // permissions so the Yard Management auto-select picks it up without
      // requiring a hard reload. (Other users still pick up their changes
      // on next sign-in/refresh.)
      await refreshPermissions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update default yard');
    } finally {
      setSavingDefaultYard(false);
    }
  };

  // Open the group picker and load available GWI360- groups
  const openGroupPicker = async () => {
    setShowGroupPicker(true);
    if (availableGwi360Groups.length > 0) return; // Already loaded

    try {
      setLoadingGwi360Groups(true);
      const groups = await permissionsService.getGwi360Groups();
      setAvailableGwi360Groups(groups);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load GWI360 groups');
    } finally {
      setLoadingGwi360Groups(false);
    }
  };

  // Add user to a 360 group
  const handleAddToGroup = async (groupId: string) => {
    if (!selectedUser) return;

    try {
      setAddingToGroup(true);
      await permissionsService.addUserToGroup(selectedUser.user_id, groupId);

      // Refresh the preview to show updated groups
      const preview = await impersonationService.previewUserPermissions(selectedUser.user_id);
      setUserPreview(preview);
      setShowGroupPicker(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to add user to group');
    } finally {
      setAddingToGroup(false);
    }
  };

  // Remove user from a 360 group
  const handleRemoveFromGroup = async (groupId: string) => {
    if (!selectedUser) return;

    try {
      setRemovingGroupId(groupId);
      await permissionsService.removeUserFromGroup(selectedUser.user_id, groupId);

      // Refresh the preview to show updated groups
      const preview = await impersonationService.previewUserPermissions(selectedUser.user_id);
      setUserPreview(preview);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove user from group');
    } finally {
      setRemovingGroupId(null);
    }
  };

  // Start impersonating a user
  const handleImpersonate = async (user: AzureADUser) => {
    try {
      setError(null);
      await startImpersonation(user.user_id);
      window.location.reload();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start impersonation');
    }
  };

  // Force a user to sign out. Their tokens stop working within ~10s; their client
  // warns them, then bounces to the login screen. One-shot — they can sign right
  // back in, which is the intent (fresh permissions / clean client state).
  const handleForceLogout = async (user: AzureADUser) => {
    try {
      setError(null);
      setForcingLogout(true);
      const result = await permissionsService.forceUserLogout(user.id, forceLogoutReason);
      setForceLogoutResult(result.message);
      setShowForceLogoutConfirm(false);
      setForceLogoutReason('');
      // Refresh the history line so it reflects the kick just fired.
      setForceLogoutStatus({
        user_key: result.user_key,
        force_logout_after: result.force_logout_after,
        requested_by: result.requested_by,
        reason: result.reason,
        created_at: new Date().toISOString(),
        // Freshly recorded: pending until the user's client actually receives it.
        delivered_at: null,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to sign the user out');
    } finally {
      setForcingLogout(false);
    }
  };

  // Signing yourself out is allowed (it's self-healing), but it deserves
  // different wording so it isn't done by accident.
  const isSelfForceLogout = !!selectedUser && !!currentUserEmail && (
    (selectedUser.user_principal_name || '').toLowerCase() === currentUserEmail ||
    (selectedUser.email || '').toLowerCase() === currentUserEmail
  );

  useEffect(() => {
    fetchUsers();
    fetchYards();
    // Azure AD sync is owned by the Sync worker (job `azure_ad_sync`); FastCMS only reads these tables.
  }, []);

  // Get the most recent sync time from all users
  const lastSyncedDisplay = React.useMemo(() => {
    const syncDates = users
      .map(u => u.last_synced)
      .filter((d): d is string => !!d)
      .map(d => new Date(d).getTime());

    if (syncDates.length === 0) return null;

    const mostRecent = Math.max(...syncDates);
    return new Date(mostRecent).toLocaleString();
  }, [users]);

  // Define columns for the TanTable
  const columns: ColumnDef<AzureADUser>[] = [
    {
      accessorKey: 'display_name',
      header: 'Display Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'user_principal_name',
      header: 'User Principal Name',
    },
    {
      accessorKey: 'job_title',
      header: 'Job Title',
    },
    {
      accessorKey: 'department',
      header: 'Department',
    },
    {
      accessorKey: 'office_location',
      header: 'Office Location',
    },
    {
      accessorKey: 'v1_username',
      header: 'V1 Username',
      cell: ({ row }) => {
        const user = row.original;
        return <span>{user.v1_username || '-'}</span>;
      },
    },
    {
      accessorKey: 'home_region',
      header: 'Home Region',
      cell: ({ row }) => {
        const user = row.original;
        return <span>{user.home_region || '-'}</span>;
      },
    },
    {
      accessorKey: 'default_yard',
      header: 'Default Yard',
      cell: ({ row }) => {
        const user = row.original;
        if (!user.default_yard) return <span>-</span>;
        const yard = availableYards.find((y) => y.recno === user.default_yard);
        return <span>{yard?.locname || `#${user.default_yard}`}</span>;
      },
    },
    {
      accessorKey: 'is_lc',
      header: 'LC',
      cell: ({ row }) => (
        row.original.is_lc
          ? <CheckIcon className="h-4 w-4 text-green-600 dark:text-green-400" aria-label="Is an LC" />
          : <span className="text-gray-400">-</span>
      ),
    },
    {
      id: 'actions',
      header: 'Details',
      cell: ({ row }) => {
        const user = row.original;

        return (
          <button
            onClick={() => handleViewDetails(user)}
            className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
          >
            View
          </button>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-heading">Users</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Azure AD users synced from your organization
            {lastSyncedDisplay && (
              <span className="ml-2 text-gray-500 dark:text-gray-500">
                &bull; Last synced: {lastSyncedDisplay}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* TanTable */}
      <TanTable<AzureADUser>
        data={users}
        columns={columns}
        loading={loading}
        settingsKey="administration-users-table"
        fieldName="administration-users-table"
        searchPlaceholder="Search users..."
        enableSearch={true}
        enableReset={false}
        enableShowHideCols={false}
        enableRowsPerPage={true}
        enableColumnFilters={true}
        defaultPageSize={25}
      />

      {/* User Details Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedUser?.display_name || 'User Details'}
        maxWidth="md"
      >
        {selectedUser && (
          <div className="space-y-6">
            {/* User Info Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                User Information
              </h3>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm text-muted">Email</dt>
                  <dd className="text-sm text-heading">{selectedUser.email}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-muted">User Principal Name</dt>
                  <dd className="text-sm text-heading">{selectedUser.user_principal_name || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-muted">Job Title</dt>
                  <dd className="text-sm text-heading">{selectedUser.job_title || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-muted">Department</dt>
                  <dd className="text-sm text-heading">{selectedUser.department || '-'}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-muted">Office Location</dt>
                  <dd className="text-sm text-heading">{selectedUser.office_location || '-'}</dd>
                </div>
              </dl>
            </div>

            {/* V1 Username Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                V1 Account Link
              </h3>

              {selectedUser.v1_username ? (
                <div className="flex items-center justify-between bg-green-50 dark:bg-green-900/20 px-3 py-2 rounded">
                  <div>
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">
                      Linked to: {selectedUser.v1_username}
                    </span>
                  </div>
                  <button
                    onClick={handleUnlinkV1User}
                    disabled={savingV1}
                    className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                  >
                    {savingV1 ? 'Unlinking...' : 'Unlink'}
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {!showV1Search ? (
                    <button
                      onClick={openV1Search}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-label bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                    >
                      <LinkIcon className="h-4 w-4 mr-2" />
                      Link V1 Account
                    </button>
                  ) : (
                    <div className="space-y-2">
                      {loadingV1Options ? (
                        <div className="flex items-center gap-2 py-2">
                          <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                          <span className="text-sm text-muted">Loading V1 users...</span>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <div className="flex-1">
                            <TypeAhead
                              options={v1Options}
                              value={selectedV1Username}
                              onChange={handleV1UserSelect}
                              placeholder="Search by name, username, or email..."
                              showSearch={true}
                              showDescriptions={true}
                              disabled={savingV1}
                              maxHeight="max-h-48"
                            />
                          </div>
                          <button
                            onClick={() => {
                              setShowV1Search(false);
                              setV1Options([]);
                              setSelectedV1Username('');
                            }}
                            className="p-2 text-muted hover:text-gray-700 dark:hover:text-gray-300 mt-0.5"
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                      {savingV1 && (
                        <p className="text-sm text-blue-600 dark:text-blue-400">
                          Linking account...
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Home Region Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                Home Region
              </h3>

              {loadingRegions ? (
                <div className="flex items-center gap-2 py-2">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="text-sm text-muted">Loading regions...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedUser.home_region || ''}
                      onChange={(e) => handleHomeRegionChange(e.target.value)}
                      disabled={savingHomeRegion}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-heading disabled:opacity-50"
                    >
                      <option value="">No region assigned</option>
                      {availableRegions.map((region) => (
                        <option key={region} value={region}>
                          {region}
                        </option>
                      ))}
                    </select>
                    {savingHomeRegion && (
                      <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />
                    )}
                  </div>

                  {(() => {
                    const homeRegionDefaultOn = selectedUser.home_region_as_default !== false;
                    const toggleDisabled = !selectedUser.home_region || savingHomeRegionDefault;
                    return (
                      <label
                        className={`flex items-center gap-2 mt-2 text-sm select-none ${
                          toggleDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                        }`}
                        title={!selectedUser.home_region ? 'Set a home region to enable this option' : undefined}
                      >
                        <button
                          type="button"
                          role="switch"
                          aria-checked={homeRegionDefaultOn}
                          aria-label="Use home region as default in filters"
                          disabled={toggleDisabled}
                          onClick={() => handleHomeRegionAsDefaultChange(!homeRegionDefaultOn)}
                          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
                            homeRegionDefaultOn ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                              homeRegionDefaultOn ? 'translate-x-4' : 'translate-x-0.5'
                            }`}
                          />
                        </button>
                        <span className="text-label">Use as default in filters</span>
                        {savingHomeRegionDefault && (
                          <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-500" />
                        )}
                      </label>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Default Yard Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                Default Yard
              </h3>

              {loadingYards ? (
                <div className="flex items-center gap-2 py-2">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="text-sm text-muted">Loading yards...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <select
                    value={selectedUser.default_yard ?? ''}
                    onChange={(e) => handleDefaultYardChange(e.target.value)}
                    disabled={savingDefaultYard}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-700 text-heading disabled:opacity-50"
                  >
                    <option value="">No default yard</option>
                    {availableYards.map((yard) => (
                      <option key={yard.recno} value={yard.recno}>
                        {yard.locname}
                      </option>
                    ))}
                  </select>
                  {savingDefaultYard && (
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />
                  )}
                </div>
              )}
            </div>

            {/* Logistics Coordinator (LC) Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                Logistics Coordinator
              </h3>
              <label
                className={`flex items-center gap-2 text-sm select-none ${
                  savingIsLc ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                }`}
              >
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!selectedUser.is_lc}
                  aria-label="Show this user in LC assignment dropdowns"
                  disabled={savingIsLc}
                  onClick={() => handleIsLcChange(!selectedUser.is_lc)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:cursor-not-allowed ${
                    selectedUser.is_lc ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      selectedUser.is_lc ? 'translate-x-4' : 'translate-x-0.5'
                    }`}
                  />
                </button>
                <span className="text-label">
                  Is an LC — show in order / quote / tablemaint LC dropdowns
                </span>
                {savingIsLc && <ArrowPathIcon className="h-4 w-4 animate-spin text-blue-500" />}
              </label>
            </div>

            {/* 360 Groups Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                  360 Groups {userPreview && `(${userPreview.groups.filter(g => g.startsWith('GWI360-')).length})`}
                </h3>
                <button
                  onClick={openGroupPicker}
                  className="p-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                  title="Add to 360 group"
                >
                  <PlusIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Group Picker Dropdown */}
              {showGroupPicker && (
                <div className="border border-rule rounded-md p-2 space-y-2 bg-panel">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted">Select a group to add:</span>
                    <button
                      onClick={() => setShowGroupPicker(false)}
                      className="p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {loadingGwi360Groups ? (
                    <div className="flex items-center gap-2 py-2">
                      <ArrowPathIcon className="h-4 w-4 animate-spin text-gray-400" />
                      <span className="text-sm text-gray-500">Loading groups...</span>
                    </div>
                  ) : (
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {availableGwi360Groups
                        .filter(g => !userPreview?.group_details?.some(ud => ud.id === g.id))
                        .map((group) => (
                          <button
                            key={group.id}
                            onClick={() => handleAddToGroup(group.id)}
                            disabled={addingToGroup}
                            className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20 text-label disabled:opacity-50"
                          >
                            {addingToGroup ? 'Adding...' : group.displayName}
                          </button>
                        ))}
                      {availableGwi360Groups.filter(g => !userPreview?.group_details?.some(ud => ud.id === g.id)).length === 0 && (
                        <p className="text-xs text-muted py-1">User is already in all GWI360 groups</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">Loading groups...</span>
                </div>
              ) : userPreview?.group_details && userPreview.group_details.filter(g => g.displayName.startsWith('GWI360-')).length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userPreview.group_details.filter(g => g.displayName.startsWith('GWI360-')).map((group) => (
                    <div
                      key={group.id}
                      className="flex items-center justify-between text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded"
                    >
                      <span>{group.displayName}</span>
                      <button
                        onClick={() => handleRemoveFromGroup(group.id)}
                        disabled={removingGroupId === group.id}
                        className="p-0.5 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                        title="Remove from group"
                      >
                        {removingGroupId === group.id ? (
                          <ArrowPathIcon className="h-4 w-4 animate-spin" />
                        ) : (
                          <MinusIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">No 360 groups found</p>
              )}
            </div>

            {/* Groups Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                Groups {userPreview && `(${userPreview.groups.length})`}
              </h3>
              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">Loading groups...</span>
                </div>
              ) : userPreview?.groups && userPreview.groups.length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userPreview.groups.map((group, idx) => (
                    <div
                      key={idx}
                      className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded"
                    >
                      {group}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">No groups found</p>
              )}
            </div>

            {/* Accessible Routes Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-heading uppercase tracking-wide">
                Accessible Routes {userPreview && `(${userPreview.accessible_routes.length})`}
              </h3>
              {loadingPreview ? (
                <div className="flex items-center justify-center py-4">
                  <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">Loading routes...</span>
                </div>
              ) : userPreview?.is_global_admin ? (
                <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-3 py-2 rounded text-sm font-medium">
                  Global Administrator - All Routes Accessible
                </div>
              ) : userPreview?.accessible_routes && userPreview.accessible_routes.length > 0 ? (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  {userPreview.accessible_routes.map((route, idx) => (
                    <div
                      key={idx}
                      className="text-sm bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-3 py-2 rounded"
                    >
                      {route}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted">No accessible routes</p>
              )}
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
              <button
                onClick={() => {
                  setDrawerOpen(false);
                  handleImpersonate(selectedUser);
                }}
                disabled={isImpersonating}
                className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              >
                <UserIcon className="h-4 w-4 mr-2" />
                {isImpersonating ? 'Starting...' : 'Impersonate This User'}
              </button>

              {/* Force sign-out. One-shot: the user can sign straight back in,
                  which is the point — it refreshes stale permissions and resets a
                  wedged client. Not an account disable, nothing to undo. */}
              {forceLogoutResult ? (
                <div className="rounded-md bg-green-50 dark:bg-green-900/30 px-3 py-2 text-sm text-green-800 dark:text-green-300">
                  {forceLogoutResult}
                  <div className="text-xs text-green-700 dark:text-green-400 mt-1">
                    Takes effect within ~10 seconds.
                  </div>
                </div>
              ) : showForceLogoutConfirm ? (
                <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 space-y-2">
                  <div className="text-sm font-medium text-red-800 dark:text-red-300">
                    {isSelfForceLogout
                      ? 'Sign yourself out? You will be bounced to the login screen.'
                      : `Sign ${selectedUser.display_name || selectedUser.email} out?`}
                  </div>
                  <p className="text-xs text-red-700 dark:text-red-400">
                    They get a 15-second warning to save their work, then their
                    session ends. They can sign back in immediately.
                  </p>
                  <input
                    type="text"
                    value={forceLogoutReason}
                    onChange={(e) => setForceLogoutReason(e.target.value)}
                    placeholder="Reason (optional)"
                    maxLength={255}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-heading"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleForceLogout(selectedUser)}
                      disabled={forcingLogout}
                      className="flex-1 inline-flex justify-center items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                    >
                      {forcingLogout ? 'Signing out...' : 'Confirm sign-out'}
                    </button>
                    <button
                      onClick={() => {
                        setShowForceLogoutConfirm(false);
                        setForceLogoutReason('');
                      }}
                      disabled={forcingLogout}
                      className="flex-1 inline-flex justify-center items-center px-3 py-1.5 rounded-md text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-highlight disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    onClick={() => setShowForceLogoutConfirm(true)}
                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 dark:border-red-800 rounded-md shadow-sm text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    Force Sign-Out
                  </button>
                  {forceLogoutStatus?.created_at && (
                    <p className="text-xs text-muted">
                      Last signed out {new Date(forceLogoutStatus.created_at).toLocaleString()}
                      {forceLogoutStatus.requested_by ? ` by ${forceLogoutStatus.requested_by}` : ''}
                      {forceLogoutStatus.reason ? ` — ${forceLogoutStatus.reason}` : ''}
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default Users;
